import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useSearchParams } from "react-router-dom";
import { InputField } from "@/design-system/Atoms/InputField";
import { Button, Typography } from "@/design-system/Atoms";
import {
    resetPasswordFormSchema,
    type ResetPasswordFormSchema,
} from "@/validation-schemas/reset-password-schemas";
import { useResetPasswordFormSubmission } from "@/rtkQuery/features/hooks/useResetPasswordFormSubmission";
import { ROUTES } from "@/routes/routes.config";

export const ResetPasswordSection = () => {
    const navigate = useNavigate();
    const { submitResetPasswordForm, isLoading } = useResetPasswordFormSubmission();

    const [searchParams] = useSearchParams();
    const rawToken = searchParams.get("token") || "";
    const token = decodeURIComponent(rawToken.replace(/ /g, "+"));

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset,
    } = useForm<ResetPasswordFormSchema>({
        resolver: zodResolver(resetPasswordFormSchema),
        defaultValues: {
            token,
        },
    });

    const onSubmit = async (data: ResetPasswordFormSchema) => {
        try {
            const payload = {
                newPassword: data.newPassword,
                confirmPassword: data.confirmPassword,
                token: data.token,
                email: data.email,
            };
            const result = await submitResetPasswordForm(payload);
            if (result?.success) {
                reset({ newPassword: "", confirmPassword: "", token });
                navigate(ROUTES.LOGIN, {
                    replace: true,
                });
            }
        } catch (err) {
            console.error("Reset failed", err);
        }
    };

    return (
        <section id="login-feature" className="contact-form">
            <div className="flex flex-wrap">
                <div className="w-full xl:w-1/2 px-2">
                    <div className="my-[4.68rem] lg:my-40">
                        <div className="container mx-auto px-4">
                            <div className="text-center mb-10">
                                <Typography variant="sectionHeading" as="h2" className="mb-5">
                                    Reset Password
                                </Typography>
                            </div>
                            <div className="bg-[#f6f7f9] p-6 rounded-[20px] lg:w-8/12 mx-auto">
                                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                        <InputField
                                            placeholder="Email"
                                            type="email"
                                            register={register("email")}
                                            error={errors.email?.message}
                                            className="md:col-span-2"
                                        />
                                        <InputField
                                            placeholder="New Password"
                                            type="password"
                                            register={register("newPassword")}
                                            error={errors.newPassword?.message}
                                            className="md:col-span-2"
                                        />
                                        <InputField
                                            placeholder="Confirm Password"
                                            type="password"
                                            register={register("confirmPassword")}
                                            error={errors.confirmPassword?.message}
                                            className="md:col-span-2"
                                        />
                                    </div>

                                    <input
                                        type="hidden"
                                        value={token}
                                        {...register("token")}
                                    />

                                    <Button
                                        variant="secondary"
                                        size="xl"
                                        className="!w-full"
                                        type="submit"
                                        disabled={isSubmitting || isLoading}
                                        loading={isSubmitting || isLoading}
                                    >
                                        {isLoading ? "Submitting..." : "Reset Password"}
                                    </Button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="w-full xl:w-1/2 p-0">
                    <img
                        src="/images/login/img-signup.png"
                        alt=""
                        className="w-full h-full object-cover align-middle"
                    />
                </div>
            </div>
        </section>
    );
};
